import { DeleteOutlined, PlusOutlined, CommentOutlined } from "@ant-design/icons";
import { Button, Card, Col, DatePicker, Divider, Form, Input, InputNumber, Modal, notification, Popconfirm, Row, Select, Space, Table } from "antd";
import {
  IOU_ATTACHMENT_UPLOAD,
  IOU_COST_ADD_ENDPOINT,
  IOU_COST_CREATE_ENDPOINT,
  IOU_COST_EDIT_ENDPOINT,
  IOU_COST_STORE_ENDPOINT,
  IOU_DOCUMENTS_LIST,
  IOU_IMPORT_RA_EXPENDITURE,
  IOU_IMPORT_RA_EXPENDITURE_STORE,
  IOU_IMPORT_NRA_EXPENDITURE,
  IOU_IMPORT_NRA_EXPENDITURE_STORE,
  IOU_PURPOSE_LIST_ENDPOINT,
  IOU_WISE_DOCUMENT,
  IOU_DOCUMENT_GET_ENDPOINT,
  IOU_ADJUSTMENT_STORE,
  IOU_ADJUSTMENT_IMPORT_EDIT_ENDPOINT,
  IOU_ADJUSTMENT_LIST,
  SALES_CONTRACT_LIST,
} from "apiServices/API_ENDPOINTS";
import { getData, postData, putData } from "apiServices/common";
import moment from "moment";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import UploadDocument from "./UploadDocument";
import IOUDocumentUpload from "../IOUCost/IOUDocumentUpload";

const { TextArea } = Input;
const { Option } = Select;

const mergeInvoiceLists = (...lists) => {
  const byId = new Map();
  lists.flat().forEach((invoice) => {
    if (invoice?.id == null) return;
    const id = Number(invoice.id);
    if (Number.isNaN(id)) return;
    byId.set(id, { ...invoice, id });
  });
  return Array.from(byId.values());
};

/**
 * Main Component
 */
const IOUAdjustmentImportForm = forwardRef((props, ref) => {
  const [userType, setUserType] = useState("");
  const [purposeList, setPurposeList] = useState([]);
  const [purposeName, setPurposeName] = useState("");
  const { form, isEdit, editInfo, viewMode, getIOUCostInfo, selectedIouData, onSubmittingChange } = props;
  const [filesList, setFileList] = useState([]);

  const [selectedInvoiceIds, setSelectedInvoiceIds] = useState([]);
  const [invoiceExpNumbers, setInvoiceExpNumbers] = useState({});
  const [receiptableRows, setReceiptableRows] = useState({});
  const [nonReceiptableRows, setNonReceiptableRows] = useState({});
  const [invoices, setInvoices] = useState([]);
  const [remarksValue, setRemarksValue] = useState("");
  const [loading, setLoading] = useState({
    salesContracts: false,
    invoices: false,
  });
  const [expenditureTypeMap, setExpenditureTypeMap] = useState({
    ra: {},
    nra: {},
  });
  const [raExpenditureList, setRaExpenditureList] = useState([]);
  const [nraExpenditureList, setNraExpenditureList] = useState([]);
  const [newRaExpenditureName, setNewRaExpenditureName] = useState({});
  const [newNraExpenditureName, setNewNraExpenditureName] = useState({});
  const [iouList, setIouList] = useState([]);
  const [usedIouIds, setUsedIouIds] = useState(new Set());
  const [loadingIouList, setLoadingIouList] = useState(false);
  const [selectedIouId, setSelectedIouId] = useState(null);
  const [commentModal, setCommentModal] = useState({
    visible: false,
    invoiceId: null,
    rowId: null,
    type: null, // 'ra' or 'nra'
    comment: "",
  });
  const [invoiceBillEntryNos, setInvoiceBillEntryNos] = useState({});
  const [invoiceDocReceiveDates, setInvoiceDocReceiveDates] = useState({});
  const [invoiceBillAdjustDates, setInvoiceBillAdjustDates] = useState({});
  const [salesContractList, setSalesContractList] = useState([]);
  const [loadingSalesContracts, setLoadingSalesContracts] = useState(false);
  const [invoiceSalesContractIds, setInvoiceSalesContractIds] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);
  const editInvoicesHydratedRef = useRef(null);

  const navigate = useNavigate();

  const setSubmittingState = useCallback(
    (value) => {
      submittingRef.current = value;
      setSubmitting(value);
      if (onSubmittingChange) {
        onSubmittingChange(value);
      }
    },
    [onSubmittingChange],
  );

  useEffect(() => {
    const username = localStorage.getItem("name") || "";

    const roleInfoStr = localStorage.getItem("roleInfo") || "{}";
    let roleInfo;
    try {
      roleInfo = JSON.parse(roleInfoStr);
    } catch (e) {
      roleInfo = {};
    }

    const userRole = roleInfo.name || "";
    setUserType(userRole);

    if (!isEdit) {
      const defaultValues = {
        iou_date: moment(),
        taken_by: username,
        nature_of_payments: "NRA",
        requested_on: moment(),
        approved_ra: 0,
        approved_nra: 0,
        total_approve_amount: 0,
        bill_amount_ra: 0,
        bill_amount_nra: 0,
        total_bill_amount: 0,
        adjustment_ra: 0,
        adjustment_nra: 0,
        adjustment_date: moment(),
        remarks: "",
      };

      if (userRole === "Head of Commercial & Shipping") {
        defaultValues.iou_for = "CGL";
        defaultValues.nature_of_payments = "RA";
      } else if (userRole === "Director") {
        defaultValues.iou_for = "OTL";
      }

      form.setFieldsValue(defaultValues);
    }
  }, [isEdit, form, props.currentUser]);

  useEffect(() => {
    if (editInfo && editInfo.id) {
      const approvedRA = parseFloat(editInfo?.approved_ra || 0);
      const approvedNRA = parseFloat(editInfo?.approved_nra || 0);
      const totalApproveAmount = parseFloat(editInfo?.total_approve_amount || 0);
      const billAmountRA = parseFloat(editInfo?.bill_amount_ra || 0);
      const billAmountNRA = parseFloat(editInfo?.bill_amount_nra || 0);
      const totalBillAmount = parseFloat(editInfo?.total_bill_amount || 0);
      const adjustmentRA = parseFloat(editInfo?.adjustment_ra || 0);
      const adjustmentNRA = parseFloat(editInfo?.adjustment_nra || 0);

      // Find IOU details from the list if available
      let iouDate = moment();
      const editIouId = editInfo?.iou_id != null ? Number(editInfo.iou_id) : undefined;
      if (editIouId && iouList.length > 0) {
        const selectedIou = iouList.find((iou) => Number(iou.id) === editIouId);
        if (selectedIou) {
          iouDate = selectedIou.iou_date ? moment(selectedIou.iou_date) : moment();
        } else if (editInfo?.iou_info?.iou_date) {
          iouDate = moment(editInfo.iou_info.iou_date);
        }
      } else if (editInfo?.iou_info?.iou_date) {
        iouDate = moment(editInfo.iou_info.iou_date);
      }

      setSelectedIouId(editIouId);
      const iouFor =
        editInfo?.iou_info?.iou_for ||
        editInfo?.iou_for ||
        (iouList.find((iou) => Number(iou.id) === editIouId)?.iou_for ?? undefined);
      form.setFieldsValue({
        iou_id: editIouId,
        iou_for: iouFor,
        ra_amount: approvedRA,
        nra_amount: approvedNRA,
        iou_amount: totalApproveAmount,
        approved_ra: approvedRA,
        approved_nra: approvedNRA,
        total_approve_amount: totalApproveAmount,
        bill_amount_ra: billAmountRA,
        bill_amount_nra: billAmountNRA,
        total_bill_amount: totalBillAmount,
        adjustment_ra: adjustmentRA,
        adjustment_nra: adjustmentNRA,
        adjustment_date: editInfo?.request_date ? moment(editInfo.request_date) : moment(),
        iou_date: iouDate,
        remarks: editInfo?.remarks || "",
      });

      // Store remarks in state for later use in submission
      if (editInfo?.remarks !== undefined && editInfo?.remarks !== null) {
        setRemarksValue(editInfo.remarks);
      }

      // Populate invoice details only once per edit record (don't undo user changes)
      if (
        editInvoicesHydratedRef.current !== editInfo.id &&
        editInfo?.adj_invoices &&
        Array.isArray(editInfo.adj_invoices)
      ) {
        editInvoicesHydratedRef.current = editInfo.id;
        const invoiceIds = editInfo.adj_invoices
          .map((adjInvoice) => (adjInvoice.invoice_id != null ? Number(adjInvoice.invoice_id) : null))
          .filter((id) => id != null && !Number.isNaN(id));
        setSelectedInvoiceIds(invoiceIds);
        // Set form field to sync with state
        form.setFieldsValue({ invoice_ids: invoiceIds });

        // Add invoice data to invoices list if not already present
        editInfo.adj_invoices.forEach((adjInvoice) => {
          if (adjInvoice.invoice || adjInvoice.import_invoice || adjInvoice.export_invoice) {
            const invoiceData = adjInvoice.invoice || adjInvoice.import_invoice || adjInvoice.export_invoice;
            setInvoices((prev) => mergeInvoiceLists(prev, [invoiceData]));
          }
        });

        // Set invoice fields
        const expNumbers = {};
        const billEntryNos = {};
        const docReceiveDates = {};
        const billAdjustDates = {};
        const salesContractIds = {};
        editInfo.adj_invoices.forEach((adjInvoice) => {
          if (adjInvoice.invoice_id && adjInvoice.exp_imp_awbl_no) {
            expNumbers[adjInvoice.invoice_id] = adjInvoice.exp_imp_awbl_no;
          }
          if (adjInvoice.invoice_id && adjInvoice.bill_of_entry_no) {
            billEntryNos[adjInvoice.invoice_id] = adjInvoice.bill_of_entry_no;
          }
          if (adjInvoice.invoice_id && adjInvoice.doc_receive_date_time) {
            docReceiveDates[adjInvoice.invoice_id] = moment(adjInvoice.doc_receive_date_time);
          }
          if (adjInvoice.invoice_id && adjInvoice.bill_adjust_date) {
            billAdjustDates[adjInvoice.invoice_id] = moment(adjInvoice.bill_adjust_date);
          }
          // Get sales contract ID from invoice data
          if (adjInvoice.invoice || adjInvoice.import_invoice) {
            const invoiceData = adjInvoice.invoice || adjInvoice.import_invoice;
            if (invoiceData.sales_contract_id) {
              salesContractIds[adjInvoice.invoice_id] = invoiceData.sales_contract_id;
            }
          }
        });
        setInvoiceExpNumbers(expNumbers);
        setInvoiceBillEntryNos(billEntryNos);
        setInvoiceDocReceiveDates(docReceiveDates);
        setInvoiceBillAdjustDates(billAdjustDates);
        setInvoiceSalesContractIds(salesContractIds);

        // Build receiptable rows (RA) for each invoice
        const receiptableRowsData = {};
        const nonReceiptableRowsData = {};

        editInfo.adj_invoices.forEach((adjInvoice) => {
          const invoiceId = adjInvoice.invoice_id;

          // Process RA details
          if (adjInvoice.invoice_ra_details && Array.isArray(adjInvoice.invoice_ra_details)) {
            receiptableRowsData[invoiceId] = adjInvoice.invoice_ra_details.map((raDetail, index) => ({
              id: Date.now() + index,
              // Use ra_expenditure_id for type selection
              type: raDetail.ra_expenditure_id || raDetail.ra_expenditure?.name || "",
              amount: parseFloat(raDetail.ra_amount || 0),
              comment: raDetail.comments || "",
            }));
          } else {
            receiptableRowsData[invoiceId] = [];
          }

          // Process NRA details
          if (adjInvoice.invoice_nra_details && Array.isArray(adjInvoice.invoice_nra_details)) {
            nonReceiptableRowsData[invoiceId] = adjInvoice.invoice_nra_details.map((nraDetail, index) => ({
              id: Date.now() + 1000 + index,
              // Use nra_expenditure_id for type selection
              type: nraDetail.nra_expenditure_id || nraDetail.nra_expenditure?.name || "",
              amount: parseFloat(nraDetail.nra_amount || 0),
              comment: nraDetail.comments || "",
            }));
          } else {
            nonReceiptableRowsData[invoiceId] = [];
          }
        });

        setReceiptableRows(receiptableRowsData);
        setNonReceiptableRows(nonReceiptableRowsData);
      }

      // Documents are now fetched from API using IOU ID, not from editInfo.attachments
      // Removed setFileList here to avoid overwriting API-fetched documents
    } else if (selectedIouData && !isEdit) {
      const approvedRA = parseFloat(selectedIouData.ra_amount || 0);
      const approvedNRA = parseFloat(selectedIouData.nra_amount || 0);
      const totalApproveAmount = approvedRA + approvedNRA;

      form.setFieldsValue({
        iou_for: selectedIouData.iou_for,
        iou_number: selectedIouData.iou_number,
        iou_date: selectedIouData.iou_date ? moment(selectedIouData.iou_date) : moment(),
        approved_ra: approvedRA,
        approved_nra: approvedNRA,
        total_approve_amount: totalApproveAmount,
        bill_amount_ra: 0,
        bill_amount_nra: 0,
        total_bill_amount: 0,
        adjustment_ra: approvedRA,
        adjustment_nra: approvedNRA,
        adjustment_date: moment(),
        remarks: "",
      });
    }
  }, [editInfo, form, selectedIouData, isEdit, iouList]);

  const getIOUDocuments = useCallback(
    async (iouId) => {
      if (!iouId) {
        setFileList([]);
        return;
      }

      try {
        const query = `${IOU_DOCUMENT_GET_ENDPOINT}?iou_id=${iouId}`;
        const response = await getData(query);

        console.log("Full API response:", response);
        console.log("Response data:", response?.data);
        console.log("Response data.data:", response?.data?.data);

        if (response && (response.data?.code === 200 || response.status === 200)) {
          // Handle different response structures
          let documents = [];
          if (Array.isArray(response.data?.data)) {
            documents = response.data.data;
          } else if (Array.isArray(response.data)) {
            documents = response.data;
          } else if (response.data?.data && typeof response.data.data === 'object' && !Array.isArray(response.data.data)) {
            // Handle case where data might be an object with a data property
            documents = response.data.data.data || [];
          }

          console.log("Extracted documents array:", documents);

          const mappedDocs = documents.map((doc) => ({
            id: doc.id,
            name: doc.document_name || doc.name,
            path: doc.document || doc.path,
            status: doc.status || "created",
            comments: doc.comments || "",
          }));

          console.log("Mapped documents:", mappedDocs);
          setFileList(mappedDocs);
        } else {
          console.log("Response not successful:", response);
          setFileList([]);
        }
      } catch (error) {
        setFileList([]);
        notification.error({
          message: "Error",
          description: "Failed to fetch IOU documents",
          placement: "topRight",
        });
      }
    },
    [setFileList],
  );

  useEffect(() => {
    const iouId = isEdit ? editInfo?.iou_id : selectedIouId;
    if (iouId) {
      getIOUDocuments(iouId);
    } else {
      setFileList([]);
    }
  }, [editInfo?.iou_id, selectedIouId, isEdit, getIOUDocuments]);

  // Debug: Log when filesList changes
  useEffect(() => {
    console.log("filesList updated:", filesList);
  }, [filesList]);

  const handleSubmit = useCallback(
    async (values) => {
      if (submittingRef.current) {
        return;
      }

      console.log("🚀 ~ values:", values);
      try {
        // Get form values directly if values object is empty
        const formValues = Object.keys(values || {}).length === 0 ? form.getFieldsValue() : values;

        // Prefer current form selection; do not restore cleared invoices from editInfo
        let invoiceIds = Array.isArray(formValues.invoice_ids)
          ? formValues.invoice_ids
          : Array.isArray(selectedInvoiceIds)
            ? selectedInvoiceIds
            : [];

        // Always validate that invoice IDs are selected
        if (!invoiceIds || invoiceIds.length === 0) {
          notification.error({
            message: "Validation Error",
            description: "Please select at least one invoice",
            placement: "topRight",
          });
          return;
        }

        // Keep form field and state in sync with the selection being saved
        form.setFieldsValue({ invoice_ids: invoiceIds });
        if (
          invoiceIds.length !== selectedInvoiceIds.length ||
          invoiceIds.some((id, idx) => Number(id) !== Number(selectedInvoiceIds[idx]))
        ) {
          setSelectedInvoiceIds(invoiceIds.map((id) => Number(id)));
        }

        if (!validateReceiptableRows() || !validateNonReceiptableRows()) {
          notification.error({
            message: "Validation Error",
            description: "Amount is required and must be greater than 0 when type is selected",
            placement: "topRight",
          });
          return;
        }

        const selectedIouIdForSubmit = isEdit ? editInfo?.iou_id : formValues?.iou_id;
        if (!isEdit && usedIouIds.has(Number(selectedIouIdForSubmit))) {
          const selectedIou = iouList.find((iou) => Number(iou.id) === Number(selectedIouIdForSubmit));
          const iouNumber = selectedIou?.iou_number || "selected IOU";
          notification.error({
            message: "Validation Error",
            description: `IOU ${iouNumber} already has an import adjustment. The same IOU cannot be used again.`,
            placement: "topRight",
          });
          return;
        }

        // Calculate values for display (keep calculation as before)
        const approvedRA = parseFloat(formValues.approved_ra || 0);
        const approvedNRA = parseFloat(formValues.approved_nra || 0);
        const totalApproveAmount = approvedRA + approvedNRA;
        const billAmountRA = getTotalBillAmountRA();
        const billAmountNRA = getTotalBillAmountNRA();
        const totalBillAmount = billAmountRA + billAmountNRA;
        const adjustmentRA = approvedRA - billAmountRA;
        const adjustmentNRA = approvedNRA - billAmountNRA;

        // FIX: Use invoiceIds from form values or state
        const invoiceDetails = invoiceIds.map((invoiceId) => {
          const invoice = invoices.find((inv) => Number(inv.id) === Number(invoiceId)) || {};
          const invoiceReceiptableRows = receiptableRows[invoiceId] || [];
          const invoiceNonReceiptableRows = nonReceiptableRows[invoiceId] || [];

          const raExpenditureDetails = invoiceReceiptableRows
            .filter((row) => row.type && row.amount)
            .map((row) => ({
              // If type is a number, use it directly as ID; otherwise map by name
              ra_expenditure_id: typeof row.type === "number" ? row.type : expenditureTypeMap.ra[row.type] || null,
              ra_amount: parseFloat(row.amount) || 0,
              comments: row.comment || "",
            }));

          const nraExpenditureDetails = invoiceNonReceiptableRows
            .filter((row) => row.type && row.amount)
            .map((row) => ({
              // If type is a number, use it directly as ID; otherwise map by name
              nra_expenditure_id: typeof row.type === "number" ? row.type : expenditureTypeMap.nra[row.type] || null,
              nra_amount: parseFloat(row.amount) || 0,
              comments: row.comment || "",
            }));

          const billOfEntryNo = invoiceBillEntryNos[invoiceId] !== undefined && invoiceBillEntryNos[invoiceId] !== null
            ? String(invoiceBillEntryNos[invoiceId]).trim()
            : invoice.bill_of_entry_no || invoice.bill_entry_no || "";

          return {
            invoice_id: invoiceId,
            exp_imp_awbl_no: invoiceExpNumbers[invoiceId] ? String(invoiceExpNumbers[invoiceId]).trim() : "",
            doc_receive_date_time: invoiceDocReceiveDates[invoiceId]
              ? invoiceDocReceiveDates[invoiceId].format("YYYY-MM-DD HH:mm:ss")
              : "",
            bill_adjust_date: invoiceBillAdjustDates[invoiceId]
              ? invoiceBillAdjustDates[invoiceId].format("YYYY-MM-DD")
              : moment().format("YYYY-MM-DD"),
            bill_of_entry_no: billOfEntryNo ? String(billOfEntryNo).trim() : "",
            ra_expenditure_details: raExpenditureDetails,
            nra_expenditure_details: nraExpenditureDetails,
          };
        });

        // Get remarks from form field, state, or editInfo in edit mode
        const finalRemarksValue =
          formValues.remarks !== undefined && formValues.remarks !== null && formValues.remarks !== ""
            ? formValues.remarks
            : remarksValue !== undefined && remarksValue !== null && remarksValue !== ""
            ? remarksValue
            : isEdit
            ? editInfo?.remarks || ""
            : "";

        // In edit mode, use original values from API; otherwise use calculated values
        const payload = {
          iou_id: isEdit ? editInfo?.iou_id : formValues?.iou_id,
          approved_ra: isEdit ? editInfo?.approved_ra?.toString() || approvedRA.toString() : approvedRA.toString(),
          approved_nra: isEdit ? editInfo?.approved_nra?.toString() || approvedNRA.toString() : approvedNRA.toString(),
          total_approve_amount: isEdit
            ? editInfo?.total_approve_amount?.toString() || totalApproveAmount.toString()
            : totalApproveAmount.toString(),
          bill_amount_ra: billAmountRA.toString(),
          bill_amount_nra: billAmountNRA.toString(),
          total_bill_amount: totalBillAmount.toString(),
          adjustment_ra: isEdit
            ? editInfo?.adjustment_ra?.toString() || adjustmentRA.toString()
            : adjustmentRA.toString(),
          adjustment_nra: isEdit
            ? editInfo?.adjustment_nra?.toString() || adjustmentNRA.toString()
            : adjustmentNRA.toString(),
          adjustment_for: "import",
          remarks: finalRemarksValue,
          invoice_details: invoiceDetails,
        };

        const query = isEdit ? `${IOU_ADJUSTMENT_LIST}/${editInfo?.id}` : IOU_ADJUSTMENT_STORE;

        setSubmittingState(true);
        const response = isEdit ? await putData(query, payload) : await postData(query, payload);

        if (response && (response?.code === 200 || response?.status === 200)) {
          notification.success({
            message: "Success",
            description:
              response?.message || `IOU Adjustment for Import ${isEdit ? "updated" : "created"} successfully`,
            placement: "bottomRight",
          });
          navigate("/import-export-expense-tracking/iou-adjustment-for-import");
        } else {
          const errorMessage = Array.isArray(response?.message)
            ? response.message.join(", ")
            : response?.message || "Failed to submit IOU Adjustment for Import form";
          notification.error({
            message: "Error",
            description: errorMessage,
            placement: "topRight",
          });
        }
      } catch (error) {
        notification.error({
          message: "Error",
          description: error?.message || "Failed to submit IOU Adjustment for Import form",
          placement: "topRight",
        });
      } finally {
        setSubmittingState(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      isEdit,
      editInfo,
      navigate,
      receiptableRows,
      nonReceiptableRows,
      selectedInvoiceIds,
      invoiceExpNumbers,
      invoiceBillEntryNos,
      invoiceDocReceiveDates,
      invoiceBillAdjustDates,
      expenditureTypeMap,
      remarksValue,
      submitting,
      setSubmittingState,
      usedIouIds,
      iouList,
    ],
  );

  const getIOUNumber = useCallback(async (val) => {
    let url = val ? `${IOU_COST_CREATE_ENDPOINT}?type=${val}` : IOU_COST_CREATE_ENDPOINT;
    try {
      const response = await getData(url);
      if (response && response.status === 200) {
        form.setFieldsValue({ iou_number: response.data.data });
      } else {
        notification.error({
          message: "Error",
          description: "Failed to generate IOU number",
          placement: "topRight",
        });
        return "";
      }
    } catch (error) {
      notification.error({
        message: "Error",
        description: "Failed to generate IOU number",
        placement: "topRight",
      });
      return "";
    }
  }, []);

  useImperativeHandle(ref, () => ({ form }), [form]);

  const getIOUPurposeList = useCallback(async () => {
    try {
      const bodyData = {
        per_page: 1000,
      };
      const response = await getData(IOU_PURPOSE_LIST_ENDPOINT, false, bodyData);
      if (response && response.status === 200) {
        setPurposeList(response?.data?.data?.data);
        return response.data.data;
      }
    } catch (error) {
      notification.error({
        message: "Error",
        description: "Failed to fetch IOU Purpose List",
        placement: "topRight",
      });
      return [];
    }
  }, []);

  const getInvoiceNumbers = useCallback(async (searchText = "") => {
    setLoading((prev) => ({ ...prev, invoices: true }));
    try {
      const query = searchText
        ? `/api/accounting/import-details?invoice_number=${encodeURIComponent(searchText)}`
        : `/api/accounting/import-details`;
      const response = await getData(query);
      const ok = response?.status === 200 || response?.data?.code === 200;
      if (ok) {
        const invoiceData = Array.isArray(response?.data?.data?.data)
          ? response.data.data.data
          : Array.isArray(response?.data?.data)
            ? response.data.data
            : [];
        setInvoices((prev) => {
          const merged = mergeInvoiceLists(invoiceData, prev);
          return merged.sort((a, b) =>
            String(a.invoice_number || "").localeCompare(String(b.invoice_number || ""), undefined, {
              numeric: true,
              sensitivity: "base",
            }),
          );
        });
      }
    } finally {
      setLoading((prev) => ({ ...prev, invoices: false }));
    }
  }, []);

  const fetchSalesContracts = useCallback(async () => {
    setLoadingSalesContracts(true);
    try {
      const bodyData = {
        page: 1,
        per_page: 100000,
      };
      const response = await getData(SALES_CONTRACT_LIST, false, bodyData);
      if (response && (response?.status === 200 || response?.data?.code === 200)) {
        const contracts = Array.isArray(response?.data?.data?.data)
          ? response.data.data.data
          : Array.isArray(response?.data?.data)
          ? response.data.data
          : Array.isArray(response?.data)
          ? response.data
          : [];
        setSalesContractList(contracts);
      } else {
        setSalesContractList([]);
      }
    } catch (error) {
      notification.error({
        message: "Error",
        description: "Failed to fetch Sales Contracts",
        placement: "topRight",
      });
    } finally {
      setLoadingSalesContracts(false);
    }
  }, []);

  const getSalesContractLabel = useCallback((contract) => {
    return (
      contract?.reference_no ||
      contract?.sale_contract_info?.reference_no ||
      contract?.sales_contract_number ||
      (contract?.id != null ? `Contract ${contract.id}` : "")
    );
  }, []);

  const salesContractOptions = useMemo(() => {
    const map = new Map();

    salesContractList.forEach((contract) => {
      if (contract?.id != null) {
        map.set(Number(contract.id), {
          id: Number(contract.id),
          label: getSalesContractLabel(contract),
        });
      }
    });

    selectedInvoiceIds.forEach((invoiceId) => {
      const invoice = invoices.find((inv) => Number(inv.id) === Number(invoiceId));
      const contractId = invoice?.sales_contract_id;
      if (contractId != null && !map.has(Number(contractId))) {
        map.set(Number(contractId), {
          id: Number(contractId),
          label:
            invoice?.sale_contract_info?.reference_no ||
            invoice?.sales_contract_number ||
            `Contract ${contractId}`,
        });
      }
    });

    return Array.from(map.values());
  }, [salesContractList, selectedInvoiceIds, invoices, getSalesContractLabel]);

  const fetchRAExpenditureTypes = useCallback(async (searchName = "") => {
    try {
      const params = new URLSearchParams({ per_page: "100" });
      if (searchName) {
        params.set("name", searchName);
      }
      const query = `${IOU_IMPORT_RA_EXPENDITURE}?${params.toString()}`;
      const response = await getData(query);

      let raList = [];
      if (response?.status === 200) {
        if (Array.isArray(response?.data)) {
          raList = response.data;
        } else if (Array.isArray(response?.data?.data)) {
          raList = response.data.data;
        } else if (Array.isArray(response?.data?.data?.data)) {
          raList = response.data.data.data;
        }
      }

      setRaExpenditureList(raList);

      const raMap = {};
      raList.forEach((item) => {
        if (item.name) {
          raMap[item.name] = item.id;
        }
      });
      setExpenditureTypeMap((prev) => ({ ...prev, ra: raMap }));
    } catch (error) {}
  }, []);

  const fetchNRAExpenditureTypes = useCallback(async (searchName = "") => {
    try {
      const params = new URLSearchParams({ per_page: "100" });
      if (searchName) {
        params.set("name", searchName);
      }
      const query = `${IOU_IMPORT_NRA_EXPENDITURE}?${params.toString()}`;
      const response = await getData(query);

      let nraList = [];
      if (response?.status === 200) {
        if (Array.isArray(response?.data)) {
          nraList = response.data;
        } else if (Array.isArray(response?.data?.data)) {
          nraList = response.data.data;
        } else if (Array.isArray(response?.data?.data?.data)) {
          nraList = response.data.data.data;
        }
      }

      setNraExpenditureList(nraList);

      const nraMap = {};
      nraList.forEach((item) => {
        if (item.name) {
          nraMap[item.name] = item.id;
        }
      });
      setExpenditureTypeMap((prev) => ({ ...prev, nra: nraMap }));
    } catch (error) {}
  }, []);

  const createRAExpenditureType = useCallback(
    async (name, invoiceId) => {
      try {
        const response = await postData(IOU_IMPORT_RA_EXPENDITURE_STORE, { name });

        if (response && (response?.code === 200 || response?.status === 200)) {
          notification.success({
            message: "Success",
            description: "RA Expenditure type created successfully",
            placement: "topRight",
          });

          await fetchRAExpenditureTypes();

          setNewRaExpenditureName((prev) => ({ ...prev, [invoiceId]: "" }));
          return response?.data?.data;
        } else {
          notification.error({
            message: "Error",
            description: "Failed to create RA Expenditure type",
            placement: "topRight",
          });
        }
      } catch (error) {
        notification.error({
          message: "Error",
          description: "Failed to create RA Expenditure type",
          placement: "topRight",
        });
      }
      return null;
    },
    [fetchRAExpenditureTypes],
  );

  const createNRAExpenditureType = useCallback(
    async (name, invoiceId) => {
      try {
        const response = await postData(IOU_IMPORT_NRA_EXPENDITURE_STORE, { name });

        if (response && (response?.code === 200 || response?.status === 200)) {
          notification.success({
            message: "Success",
            description: "NRA Expenditure type created successfully",
            placement: "topRight",
          });

          await fetchNRAExpenditureTypes();

          setNewNraExpenditureName((prev) => ({ ...prev, [invoiceId]: "" }));
          return response?.data?.data;
        } else {
          notification.error({
            message: "Error",
            description: "Failed to create NRA Expenditure type",
            placement: "topRight",
          });
        }
      } catch (error) {
        notification.error({
          message: "Error",
          description: "Failed to create NRA Expenditure type",
          placement: "topRight",
        });
      }
      return null;
    },
    [fetchNRAExpenditureTypes],
  );

  const fetchUsedIouAdjustments = useCallback(async () => {
    try {
      const response = await getData(IOU_ADJUSTMENT_LIST, false, {
        adjustment_for: "import",
        per_page: 100000,
      });

      if (response?.status === 200) {
        const data = response.data?.data?.data || [];
        const ids = new Set(
          data.map((item) => Number(item.iou_id)).filter((id) => !Number.isNaN(id) && id > 0),
        );
        setUsedIouIds(ids);
      }
    } catch (error) {}
  }, []);

  const fetchIOUList = useCallback(async () => {
    setLoadingIouList(true);
    try {
      const response = await getData(IOU_DOCUMENTS_LIST);
      if (response && response?.status === 200) {
        const data = Array.isArray(response.data?.data) ? response.data.data : [];
        setIouList(data);
      } else {
        notification.error({
          message: "Error",
          description: "Failed to fetch IOU list",
          placement: "topRight",
        });
      }
    } catch (error) {
      notification.error({
        message: "Error",
        description: "Failed to fetch IOU list",
        placement: "topRight",
      });
    } finally {
      setLoadingIouList(false);
    }
  }, []);

  // Edit mode: selected IOU may be missing from afterDisburse list — keep it so Select shows iou_number not id
  const iouDropdownOptions = useMemo(() => {
    const byId = new Map();

    (iouList || []).forEach((iou) => {
      if (iou?.id == null) return;
      const id = Number(iou.id);
      byId.set(id, {
        ...iou,
        id,
        iou_number: iou.iou_number,
      });
    });

    if (isEdit && editInfo?.iou_id != null) {
      const id = Number(editInfo.iou_id);
      const fromInfo = editInfo?.iou_info || {};
      const iouNumber =
        fromInfo.iou_number || editInfo?.iou_number || byId.get(id)?.iou_number || null;

      if (!byId.has(id)) {
        byId.set(id, {
          ...fromInfo,
          id,
          iou_number: iouNumber || String(id),
        });
      } else if (iouNumber) {
        byId.set(id, {
          ...byId.get(id),
          iou_number: iouNumber,
        });
      }
    }

    return Array.from(byId.values());
  }, [iouList, isEdit, editInfo]);

  const handleIouSelect = useCallback(
    (iouId) => {
      setSelectedIouId(iouId);
      if (!iouId) {
        form.setFieldsValue({
          iou_for: undefined,
          iou_number: undefined,
          iou_date: moment(),
          approved_ra: 0,
          approved_nra: 0,
          total_approve_amount: 0,
          bill_amount_ra: 0,
          bill_amount_nra: 0,
          total_bill_amount: 0,
          adjustment_ra: 0,
          adjustment_nra: 0,
          adjustment_date: moment(),
        });
        setFileList([]);
        return;
      }

      const selectedIou = iouDropdownOptions.find((iou) => Number(iou.id) === Number(iouId));
      if (selectedIou) {
        const approvedRA = parseFloat(selectedIou.ra_amount || 0);
        const approvedNRA = parseFloat(selectedIou.nra_amount || 0);
        const totalApproveAmount = approvedRA + approvedNRA;

        form.setFieldsValue({
          iou_for: selectedIou.iou_for,
          iou_number: selectedIou.iou_number,
          iou_date: selectedIou.iou_date ? moment(selectedIou.iou_date) : moment(),
          approved_ra: approvedRA,
          approved_nra: approvedNRA,
          total_approve_amount: totalApproveAmount,
          bill_amount_ra: 0,
          bill_amount_nra: 0,
          total_bill_amount: 0,
          adjustment_ra: approvedRA,
          adjustment_nra: approvedNRA,
          adjustment_date: moment(),
        });
        
        // Fetch documents for the selected IOU
        getIOUDocuments(iouId);
      }
    },
    [iouDropdownOptions, form, getIOUDocuments],
  );

  useEffect(() => {
    getInvoiceNumbers();
    fetchRAExpenditureTypes();
    fetchNRAExpenditureTypes();
    fetchIOUList();
    fetchUsedIouAdjustments();
    fetchSalesContracts();
  }, [
    getInvoiceNumbers,
    fetchRAExpenditureTypes,
    fetchNRAExpenditureTypes,
    fetchIOUList,
    fetchUsedIouAdjustments,
    fetchSalesContracts,
  ]);

  useEffect(() => {
    getIOUPurposeList();
  }, [getIOUPurposeList]);

  useEffect(() => {
    const approvedRA = parseFloat(form.getFieldValue("approved_ra") || 0);
    const approvedNRA = parseFloat(form.getFieldValue("approved_nra") || 0);
    const totalApproveAmount = approvedRA + approvedNRA;
    const billAmountRA = getTotalBillAmountRA();
    const billAmountNRA = getTotalBillAmountNRA();
    const totalBillAmount = billAmountRA + billAmountNRA;
    const adjustmentRA = approvedRA - billAmountRA;
    const adjustmentNRA = approvedNRA - billAmountNRA;

    form.setFieldsValue({
      total_approve_amount: totalApproveAmount,
      bill_amount_ra: billAmountRA,
      bill_amount_nra: billAmountNRA,
      total_bill_amount: totalBillAmount,
      adjustment_ra: adjustmentRA,
      adjustment_nra: adjustmentNRA,
    });
  }, [receiptableRows, nonReceiptableRows, selectedInvoiceIds]);

  const addPurpose = useCallback(
    async (purposeName) => {
      try {
        const response = await postData(IOU_COST_STORE_ENDPOINT, {
          name: purposeName,
        });

        if (response && response.code === 200) {
          getIOUPurposeList();
        } else {
          notification.error({
            message: "Error",
            description: "Failed to add purpose",
            placement: "topRight",
          });
        }
      } catch (error) {
        notification.error({
          message: "Error",
          description: "Failed to add purpose",
          placement: "topRight",
        });
      }
    },
    [getIOUPurposeList],
  );

  const addReceiptableRow = (invoiceId) => {
    const newRow = {
      id: Date.now(),
      type: undefined,
      amount: undefined,
      comment: "",
    };
    setReceiptableRows((prev) => ({
      ...prev,
      [invoiceId]: [...(prev[invoiceId] || []), newRow],
    }));
  };

  const addNonReceiptableRow = (invoiceId) => {
    const newRow = {
      id: Date.now(),
      type: undefined,
      amount: undefined,
      comment: "",
    };
    setNonReceiptableRows((prev) => ({
      ...prev,
      [invoiceId]: [...(prev[invoiceId] || []), newRow],
    }));
  };

  const updateReceiptableRow = (invoiceId, id, field, value) => {
    setReceiptableRows((prev) => ({
      ...prev,
      [invoiceId]: (prev[invoiceId] || []).map((row) => (row.id === id ? { ...row, [field]: value } : row)),
    }));
  };

  const deleteReceiptableRow = (invoiceId, id) => {
    setReceiptableRows((prev) => ({
      ...prev,
      [invoiceId]: (prev[invoiceId] || []).filter((row) => row.id !== id),
    }));
  };

  const updateNonReceiptableRow = (invoiceId, id, field, value) => {
    setNonReceiptableRows((prev) => ({
      ...prev,
      [invoiceId]: (prev[invoiceId] || []).map((row) => (row.id === id ? { ...row, [field]: value } : row)),
    }));
  };

  const deleteNonReceiptableRow = (invoiceId, id) => {
    setNonReceiptableRows((prev) => ({
      ...prev,
      [invoiceId]: (prev[invoiceId] || []).filter((row) => row.id !== id),
    }));
  };

  const getSelectedTypes = (invoiceId) => {
    return (receiptableRows[invoiceId] || [])
      .map((row) => row.type)
      .filter((type) => type !== undefined && type !== null);
  };

  const getSelectedNonReceiptableTypes = (invoiceId) => {
    return (nonReceiptableRows[invoiceId] || [])
      .map((row) => row.type)
      .filter((type) => type !== undefined && type !== null);
  };

  const getTotalReceiptable = (invoiceId) => {
    return (receiptableRows[invoiceId] || []).reduce((sum, row) => {
      const amount = parseFloat(row.amount) || 0;
      return sum + amount;
    }, 0);
  };

  const getTotalNonReceiptable = (invoiceId) => {
    return (nonReceiptableRows[invoiceId] || []).reduce((sum, row) => {
      const amount = parseFloat(row.amount) || 0;
      return sum + amount;
    }, 0);
  };

  const getTotalBillAmountRA = () => {
    return selectedInvoiceIds.reduce((total, invoiceId) => {
      return total + getTotalReceiptable(invoiceId);
    }, 0);
  };

  const getTotalBillAmountNRA = () => {
    return selectedInvoiceIds.reduce((total, invoiceId) => {
      return total + getTotalNonReceiptable(invoiceId);
    }, 0);
  };

  const validateReceiptableRows = () => {
    for (const invoiceId of selectedInvoiceIds) {
      const rows = receiptableRows[invoiceId] || [];
      for (const row of rows) {
        if (row.type) {
          if (!row.amount || parseFloat(row.amount) <= 0) {
            return false;
          }
        }
      }
    }
    return true;
  };

  const getReceiptableRowError = (row) => {
    if (row.type && (!row.amount || parseFloat(row.amount) <= 0)) {
      return "Amount is required and must be greater than 0";
    }
    return null;
  };

  const validateNonReceiptableRows = () => {
    for (const invoiceId of selectedInvoiceIds) {
      const rows = nonReceiptableRows[invoiceId] || [];
      for (const row of rows) {
        if (row.type) {
          if (!row.amount || parseFloat(row.amount) <= 0) {
            return false;
          }
        }
      }
    }
    return true;
  };

  const getNonReceiptableRowError = (row) => {
    if (row.type && (!row.amount || parseFloat(row.amount) <= 0)) {
      return "Amount is required and must be greater than 0";
    }
    return null;
  };

  const handleInvoiceMultiSelectChange = (ids) => {
    const invoiceIds = ids || [];
    setSelectedInvoiceIds(invoiceIds);
    // Sync with form field
    form.setFieldsValue({ invoice_ids: invoiceIds });

    // Initialize bill_adjust_date to current date for new invoices and set sales contract ID from invoice
    const currentDate = moment();
    invoiceIds.forEach((invoiceId) => {
      if (!invoiceBillAdjustDates[invoiceId]) {
        setInvoiceBillAdjustDates((prev) => ({
          ...prev,
          [invoiceId]: currentDate,
        }));
      }
      // Set sales contract ID from selected invoice
      const selectedInvoice = invoices.find((inv) => Number(inv.id) === Number(invoiceId));
      if (selectedInvoice && selectedInvoice.sales_contract_id && !invoiceSalesContractIds[invoiceId]) {
        setInvoiceSalesContractIds((prev) => ({
          ...prev,
          [invoiceId]: selectedInvoice.sales_contract_id,
        }));
      }
    });

    // Auto-fill Imp/Exp No from BL number (import equivalent of exp_number)
    setInvoiceExpNumbers((prev) => {
      const updated = { ...prev };
      invoiceIds.forEach((invoiceId) => {
        if (!updated[invoiceId]) {
          const selectedInvoice = invoices.find((inv) => Number(inv.id) === Number(invoiceId));
          if (selectedInvoice?.bl_number) {
            updated[invoiceId] = selectedInvoice.bl_number;
          }
        }
      });
      Object.keys(updated).forEach((id) => {
        if (!invoiceIds.includes(Number(id))) {
          delete updated[id];
        }
      });
      return updated;
    });

    // Remove dates and bill entry nos for deselected invoices
    setInvoiceDocReceiveDates((prev) => {
      const updated = { ...prev };
      Object.keys(updated).forEach((id) => {
        if (!invoiceIds.includes(Number(id))) {
          delete updated[id];
        }
      });
      return updated;
    });
    setInvoiceBillAdjustDates((prev) => {
      const updated = { ...prev };
      Object.keys(updated).forEach((id) => {
        if (!invoiceIds.includes(Number(id))) {
          delete updated[id];
        }
      });
      return updated;
    });
    setInvoiceBillEntryNos((prev) => {
      const updated = { ...prev };
      Object.keys(updated).forEach((id) => {
        if (!invoiceIds.includes(Number(id))) {
          delete updated[id];
        }
      });
      return updated;
    });
    setInvoiceSalesContractIds((prev) => {
      const updated = { ...prev };
      Object.keys(updated).forEach((id) => {
        if (!invoiceIds.includes(Number(id))) {
          delete updated[id];
        }
      });
      return updated;
    });
  };

  const openCommentModal = (invoiceId, rowId, type, currentComment = "") => {
    setCommentModal({
      visible: true,
      invoiceId,
      rowId,
      type,
      comment: currentComment,
    });
  };

  const closeCommentModal = () => {
    setCommentModal({
      visible: false,
      invoiceId: null,
      rowId: null,
      type: null,
      comment: "",
    });
  };

  const saveComment = () => {
    const { invoiceId, rowId, type, comment } = commentModal;
    if (type === "ra") {
      updateReceiptableRow(invoiceId, rowId, "comment", comment);
    } else if (type === "nra") {
      updateNonReceiptableRow(invoiceId, rowId, "comment", comment);
    }
    closeCommentModal();
  };

  const getReceiptableColumns = (invoiceId) => [
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      width: "50%",
      render: (text, record) => {
        const selectedTypes = getSelectedTypes(invoiceId);
        const newName = newRaExpenditureName[invoiceId] || "";

        return (
          <Select
            placeholder="Select or search type"
            size="small"
            // Use expenditureTypeMap.ra for value mapping
            value={
              typeof record.type === "number"
                ? raExpenditureList.find((item) => item.id === record.type)?.name || record.type
                : record.type
            }
            onChange={(value) => {
              // If value is a name, map to id if possible
              const selectedItem = raExpenditureList.find((item) => item.name === value);
              updateReceiptableRow(invoiceId, record.id, "type", selectedItem ? selectedItem.id : value);
            }}
            disabled={viewMode}
            style={{ width: "100%" }}
            allowClear
            showSearch
            optionFilterProp="children"
            filterOption={(input, option) =>
              String(option?.children ?? "")
                .toLowerCase()
                .includes(input.toLowerCase())
            }
            onFocus={() => {
              if (!raExpenditureList.length) {
                fetchRAExpenditureTypes();
              }
            }}
            dropdownRender={(menu) => (
              <>
                {menu}
                <Divider style={{ margin: "8px 0" }} />
                <Space style={{ padding: "0 8px 4px" }}>
                  <Input
                    placeholder="Enter new type name"
                    value={newName}
                    onChange={(e) =>
                      setNewRaExpenditureName((prev) => ({
                        ...prev,
                        [invoiceId]: e.target.value,
                      }))
                    }
                    size="small"
                    onPressEnter={async () => {
                      if (newName.trim()) {
                        const newItem = await createRAExpenditureType(newName.trim(), invoiceId);
                        if (newItem) {
                          updateReceiptableRow(invoiceId, record.id, "type", newItem.id);
                        }
                      }
                    }}
                  />
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    size="small"
                    onClick={async () => {
                      if (newName.trim()) {
                        const newItem = await createRAExpenditureType(newName.trim(), invoiceId);
                        if (newItem) {
                          updateReceiptableRow(invoiceId, record.id, "type", newItem.id);
                        }
                      } else {
                        notification.warning({
                          message: "Warning",
                          description: "Please enter a type name",
                          placement: "topRight",
                        });
                      }
                    }}
                  >
                    Add
                  </Button>
                </Space>
              </>
            )}
          >
            {raExpenditureList.map((item) => (
              <Option
                key={item.id}
                value={item.name}
                disabled={selectedTypes.includes(item.id) && record.type !== item.id}
              >
                {item.name}
              </Option>
            ))}
          </Select>
        );
      },
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      width: "25%",
      render: (text, record) => {
        const error = getReceiptableRowError(record);
        return (
          <div>
            <InputNumber
              placeholder="Enter amount"
              size="small"
              disabled={viewMode}
              value={record.amount}
              onChange={(value) => updateReceiptableRow(invoiceId, record.id, "amount", value)}
              style={{ width: "100%" }}
              min={0}
              step={0.01}
              precision={2}
              status={error ? "error" : ""}
              formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
              parser={(value) => value.replace(/\$\s?|(,*)/g, "")}
            />
            {error && <div style={{ color: "#ff4d4f", fontSize: "12px", marginTop: "4px" }}>{error}</div>}
          </div>
        );
      },
    },
    {
      title: "Comment",
      key: "comment",
      width: "15%",
      render: (text, record) => {
        return (
          <Button
            type={record.comment ? "primary" : "default"}
            icon={<CommentOutlined />}
            size="small"
            onClick={() => openCommentModal(invoiceId, record.id, "ra", record.comment || "")}
            disabled={viewMode}
          >
            {record.comment ? "View" : "Add"}
          </Button>
        );
      },
    },
    {
      title: "Action",
      key: "action",
      width: "10%",
      render: (text, record) => {
        return (
          <Popconfirm
            title="Delete Row"
            description="Are you sure you want to delete this row?"
            onConfirm={() => deleteReceiptableRow(invoiceId, record.id)}
            okText="Yes"
            cancelText="No"
            okButtonProps={{ danger: true }}
            disabled={viewMode}
          >
            <Button disabled={viewMode} type="link" icon={<DeleteOutlined />} danger size="small">
              Delete
            </Button>
          </Popconfirm>
        );
      },
    },
  ];

  const getNonReceiptableColumns = (invoiceId) => [
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      width: "50%",
      render: (text, record) => {
        const selectedTypes = getSelectedNonReceiptableTypes(invoiceId);
        const newName = newNraExpenditureName[invoiceId] || "";

        return (
          <Select
            placeholder="Select or search type"
            size="small"
            disabled={viewMode}
            value={
              typeof record.type === "number"
                ? nraExpenditureList.find((item) => item.id === record.type)?.name || record.type
                : record.type
            }
            onChange={(value) => {
              const selectedItem = nraExpenditureList.find((item) => item.name === value);
              updateNonReceiptableRow(invoiceId, record.id, "type", selectedItem ? selectedItem.id : value);
            }}
            style={{ width: "100%" }}
            allowClear
            showSearch
            optionFilterProp="children"
            filterOption={(input, option) =>
              String(option?.children ?? "")
                .toLowerCase()
                .includes(input.toLowerCase())
            }
            onFocus={() => {
              if (!nraExpenditureList.length) {
                fetchNRAExpenditureTypes();
              }
            }}
            dropdownRender={(menu) => (
              <>
                {menu}
                <Divider style={{ margin: "8px 0" }} />
                <Space style={{ padding: "0 8px 4px" }}>
                  <Input
                    placeholder="Enter new type name"
                    value={newName}
                    onChange={(e) =>
                      setNewNraExpenditureName((prev) => ({
                        ...prev,
                        [invoiceId]: e.target.value,
                      }))
                    }
                    size="small"
                    onPressEnter={async () => {
                      if (newName.trim()) {
                        const newItem = await createNRAExpenditureType(newName.trim(), invoiceId);
                        if (newItem) {
                          updateNonReceiptableRow(invoiceId, record.id, "type", newItem.id);
                        }
                      }
                    }}
                  />
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    size="small"
                    onClick={async () => {
                      if (newName.trim()) {
                        const newItem = await createNRAExpenditureType(newName.trim(), invoiceId);
                        if (newItem) {
                          updateNonReceiptableRow(invoiceId, record.id, "type", newItem.id);
                        }
                      } else {
                        notification.warning({
                          message: "Warning",
                          description: "Please enter a type name",
                          placement: "topRight",
                        });
                      }
                    }}
                  >
                    Add
                  </Button>
                </Space>
              </>
            )}
          >
            {nraExpenditureList.map((item) => (
              <Option
                key={item.id}
                value={item.name}
                disabled={selectedTypes.includes(item.id) && record.type !== item.id}
              >
                {item.name}
              </Option>
            ))}
          </Select>
        );
      },
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      width: "25%",
      render: (text, record) => {
        const error = getNonReceiptableRowError(record);
        return (
          <div>
            <InputNumber
              disabled={viewMode}
              size="small"
              placeholder="Enter amount"
              value={record.amount}
              onChange={(value) => updateNonReceiptableRow(invoiceId, record.id, "amount", value)}
              style={{ width: "100%" }}
              min={0}
              step={0.01}
              precision={2}
              status={error ? "error" : ""}
              formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
              parser={(value) => value.replace(/\$\s?|(,*)/g, "")}
            />
            {error && <div style={{ color: "#ff4d4f", fontSize: "12px", marginTop: "4px" }}>{error}</div>}
          </div>
        );
      },
    },
    {
      title: "Comment",
      key: "comment",
      width: "15%",
      render: (text, record) => {
        return (
          <Button
            type={record.comment ? "primary" : "default"}
            icon={<CommentOutlined />}
            size="small"
            onClick={() => openCommentModal(invoiceId, record.id, "nra", record.comment || "")}
            disabled={viewMode}
          >
            {record.comment ? "View" : "Add"}
          </Button>
        );
      },
    },
    {
      title: "Action",
      key: "action",
      width: "10%",
      render: (text, record) => {
        return (
          <Popconfirm
            title="Delete Row"
            description="Are you sure you want to delete this row?"
            onConfirm={() => deleteNonReceiptableRow(invoiceId, record.id)}
            okText="Yes"
            cancelText="No"
            okButtonProps={{ danger: true }}
            disabled={viewMode}
          >
            <Button disabled={viewMode} type="link" icon={<DeleteOutlined />} danger size="small">
              Delete
            </Button>
          </Popconfirm>
        );
      },
    },
  ];

  return (
    <div className="containt-body">
      <Card>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          onFinishFailed={(errorInfo) => {
            notification.error({
              message: "Validation Error",
              description: "Please fill in all required fields correctly",
              placement: "topRight",
            });
          }}
          requiredMark={true}
          initialValues={{
            ra_amount: 0,
            nra_amount: 0,
            iou_amount: 0,
            requested_on: moment(),
            approved_ra: 0,
            approved_nra: 0,
            total_approve_amount: 0,
            bill_amount_ra: 0,
            bill_amount_nra: 0,
            total_bill_amount: 0,
            adjustment_ra: 0,
            adjustment_nra: 0,
            adjustment_date: moment(),
            remarks: "",
          }}
          onValuesChange={(changedValues, allValues) => {
            if ("ra_amount" in changedValues || "nra_amount" in changedValues) {
              const raAmount = allValues.ra_amount || 0;
              const nraAmount = allValues.nra_amount || 0;
              form.setFieldsValue({ iou_amount: raAmount + nraAmount });
            }

            if ("approved_ra" in changedValues || "approved_nra" in changedValues) {
              const approvedRA = parseFloat(allValues.approved_ra || 0);
              const approvedNRA = parseFloat(allValues.approved_nra || 0);
              const totalApproveAmount = approvedRA + approvedNRA;
              const billAmountRA = getTotalBillAmountRA();
              const billAmountNRA = getTotalBillAmountNRA();
              const totalBillAmount = billAmountRA + billAmountNRA;
              const adjustmentRA = approvedRA - billAmountRA;
              const adjustmentNRA = approvedNRA - billAmountNRA;

              form.setFieldsValue({
                total_approve_amount: totalApproveAmount,
                bill_amount_ra: billAmountRA,
                bill_amount_nra: billAmountNRA,
                total_bill_amount: totalBillAmount,
                adjustment_ra: adjustmentRA,
                adjustment_nra: adjustmentNRA,
              });
            }

            // Update remarks state when form field changes
            if ("remarks" in changedValues) {
              setRemarksValue(allValues.remarks || "");
            }
          }}
        >
          <Row gutter={16}>
            <Col hidden span={4}>
              <Form.Item
                label="IOU For"
                name="iou_for"
                rules={[
                  {
                    // Hidden legacy field — do not block adjustment submit with a required rule
                    required: false,
                    message: "Please select IOU For",
                  },
                ]}
              >
                <Select
                  placeholder="Select department"
                  disabled={viewMode}
                  options={[
                    { label: "CGL", value: "CGL" },
                    { label: "OTL", value: "OTL" },
                    { label: "OIL", value: "OIL" },
                  ]}
                  onChange={getIOUNumber}
                />
              </Form.Item>
            </Col>

            <Col span={4}>
              <Form.Item
                label="IOU Number"
                name="iou_id"
                rules={[
                  {
                    required: !isEdit,
                    message: "Please select an IOU number",
                  },
                ]}
              >
                <Select
                  placeholder="Select IOU Number"
                  loading={loadingIouList}
                  showSearch
                  optionFilterProp="children"
                  disabled={isEdit || viewMode}
                  allowClear
                  onChange={handleIouSelect}
                  filterOption={(input, option) =>
                    String(option?.children ?? "")
                      .toLowerCase()
                      .includes(String(input).toLowerCase())
                  }
                >
                  {iouDropdownOptions.map((iou) => (
                    <Option key={iou.id} value={Number(iou.id)}>
                      {iou.iou_number || `IOU #${iou.id}`}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col hidden span={4}>
              <Form.Item
                label="RA"
                name="ra_amount"
                rules={[
                  {
                    required: false,
                    message: "Please enter amount",
                  },
                  {
                    type: "number",
                    message: "Amount must be a number",
                  },
                ]}
                hidden
              >
                <InputNumber
                  placeholder="Enter amount"
                  style={{ width: "100%" }}
                  disabled={viewMode}
                  formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                  parser={(value) => value.replace(/\$\s?|(,*)/g, "")}
                  min={0}
                  step={0.01}
                  stringMode={false}
                  keyboard={true}
                  onKeyPress={(event) => {
                    const keyCode = event.keyCode || event.which;
                    const keyValue = String.fromCharCode(keyCode);
                    const regex = /^[0-9.]+$/;
                    if (!regex.test(keyValue)) {
                      event.preventDefault();
                    }
                  }}
                />
              </Form.Item>
            </Col>
            <Col hidden span={4}>
              <Form.Item
                label="NRA"
                hidden
                name="nra_amount"
                rules={[
                  {
                    required: false,
                    message: "Please enter amount",
                  },
                  {
                    type: "number",
                    message: "Amount must be a number",
                  },
                ]}
              >
                <InputNumber
                  placeholder="Enter amount"
                  style={{ width: "100%" }}
                  disabled={viewMode}
                  formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                  parser={(value) => value.replace(/\$\s?|(,*)/g, "")}
                  min={0}
                  step={0.01}
                  stringMode={false}
                  keyboard={true}
                  onKeyPress={(event) => {
                    const keyCode = event.keyCode || event.which;
                    const keyValue = String.fromCharCode(keyCode);
                    const regex = /^[0-9.]+$/;
                    if (!regex.test(keyValue)) {
                      event.preventDefault();
                    }
                  }}
                />
              </Form.Item>
            </Col>

            <Col hidden span={4}>
              <Form.Item
                label="Amount"
                hidden
                name="iou_amount"
                rules={[
                  {
                    required: false,
                    message: "Please enter amount",
                  },
                  {
                    type: "number",
                    message: "Amount must be a number",
                  },
                ]}
              >
                <InputNumber
                  placeholder="Enter amount"
                  style={{ width: "100%" }}
                  disabled
                  formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                  parser={(value) => value.replace(/\$\s?|(,*)/g, "")}
                  min={0}
                  step={0.01}
                  stringMode={false}
                  keyboard={true}
                  onKeyPress={(event) => {
                    const keyCode = event.keyCode || event.which;
                    const keyValue = String.fromCharCode(keyCode);
                    const regex = /^[0-9.]+$/;
                    if (!regex.test(keyValue)) {
                      event.preventDefault();
                    }
                  }}
                />
              </Form.Item>
            </Col>
            {/* <Col span={4}>
              <Form.Item label="Register Entry No" name="register_entry_no">
                <Input
                  placeholder="Enter register entry number"
                  disabled={isFieldDisabled("register_entry_no")}
                />
              </Form.Item>
            </Col> */}
            {/* <Col span={4}>
              <Form.Item
                label="Nature Of Payments"
                name="nature_of_payments"
                rules={[
                  {
                    required: true,
                    message: "Please select nature of payments",
                  },
                ]}
              >
                <Radio.Group disabled={isFieldDisabled("nature_of_payments")}>
                  <Radio value="RA">RA</Radio>
                  <Radio value="NRA">NRA</Radio>
                </Radio.Group>
              </Form.Item>
            </Col> */}

            <Col hidden span={4}>
              <Form.Item label="Requested By" name="receive_by" hidden>
                <Input placeholder="Auto filled with logged-in user" disabled={viewMode} />
              </Form.Item>
            </Col>

            {/* iou_date is rendered once under Adjustment Details (avoid duplicate Form.Item name) */}

            {/* <Col span={24}>
              <Form.Item
                label="Purpose"
                name="purpose"
                rules={[
                  {
                    required: true,
                    message: "Please enter purpose",
                  },
                ]}
              >
                <TextArea
                  rows={4}
                  placeholder="Enter purpose"
                  disabled={isFieldDisabled("purpose")}
                />
              </Form.Item>
            </Col> */}
            <Col hidden span={4}>
              <Form.Item label="Purpose" name="purpose_id" hidden>
                <Input.Group compact>
                  <Form.Item name="purpose_id" noStyle>
                    <Select
                      name="purpose_id"
                      required
                      showArrow
                      style={{
                        width: "100%",
                      }}
                      placeholder="Name"
                      size="small"
                      allowClear
                      disabled={viewMode}
                      dropdownRender={(menu) => (
                        <>
                          {menu}
                          <Divider
                            style={{
                              margin: "8px 0",
                            }}
                          />
                          <Space
                            style={{
                              padding: "0 8px 4px",
                            }}
                          >
                            <Input
                              placeholder="Purpose"
                              value={purposeName}
                              onChange={(e) => setPurposeName(e.target.value)}
                              size="small"
                            />

                            <Button
                              htmlType="submit"
                              icon={<PlusOutlined />}
                              size="small"
                              onClick={(e) => {
                                e.preventDefault();
                                if (purposeName) {
                                  addPurpose(purposeName);
                                  setPurposeName("");
                                } else {
                                  notification.warning({
                                    message: "Warning",
                                    description: "Please enter a purpose name",
                                    placement: "bottomRight",
                                  });
                                }
                              }}
                            >
                              Add
                            </Button>
                          </Space>
                        </>
                      )}
                    >
                      {purposeList?.map((purpose, idx) => (
                        <Option value={purpose.id} key={idx} id={purpose?.id} label={purpose.name}>
                          {purpose.name}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Input.Group>
              </Form.Item>
            </Col>
            <Col hidden span={4}>
              <Form.Item label="Requested On" name="requested_on">
                <DatePicker disabled style={{ width: "100%" }} format="YYYY-MM-DD" />
              </Form.Item>
            </Col>
            <Col hidden span={24}>
              <Form.Item hidden label="Comments" name="comments">
                <TextArea rows={5} style={{ resize: "none" }} placeholder="Comments" disabled={viewMode} />
              </Form.Item>
            </Col>

            {/* New Auto-populated Fields */}
            <Col span={24}>
              <Divider orientation="left">Adjustment Details</Divider>
            </Col>
            <Col span={4}>
              <Form.Item
                label="IOU Date"
                name="iou_date"
                rules={[
                  {
                    required: true,
                    message: "IOU Date is required",
                  },
                ]}
              >
                <DatePicker style={{ width: "100%" }} disabled format="YYYY-MM-DD" />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item
                label="Approved RA"
                name="approved_ra"
                rules={[
                  {
                    required: true,
                    message: "Approved RA is required",
                  },
                ]}
              >
                <InputNumber
                  placeholder="Approved RA"
                  style={{ width: "100%" }}
                  disabled
                  formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                  parser={(value) => value.replace(/\$\s?|(,*)/g, "")}
                  min={0}
                  step={0.01}
                  precision={2}
                />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item
                label="Approved NRA"
                name="approved_nra"
                rules={[
                  {
                    required: true,
                    message: "Approved NRA is required",
                  },
                ]}
              >
                <InputNumber
                  placeholder="Approved NRA"
                  style={{ width: "100%" }}
                  disabled
                  formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                  parser={(value) => value.replace(/\$\s?|(,*)/g, "")}
                  min={0}
                  step={0.01}
                  precision={2}
                />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item
                label="Total Approve Amount"
                name="total_approve_amount"
                rules={[
                  {
                    required: true,
                    message: "Total Approve Amount is required",
                  },
                ]}
              >
                <InputNumber
                  placeholder="Total Approve Amount"
                  style={{ width: "100%" }}
                  disabled
                  formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                  parser={(value) => value.replace(/\$\s?|(,*)/g, "")}
                  min={0}
                  step={0.01}
                  precision={2}
                />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item
                label="Bill Amount RA"
                name="bill_amount_ra"
                rules={[
                  {
                    required: true,
                    message: "Bill Amount RA is required",
                  },
                ]}
              >
                <InputNumber
                  placeholder="Bill Amount RA"
                  style={{ width: "100%" }}
                  disabled
                  formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                  parser={(value) => value.replace(/\$\s?|(,*)/g, "")}
                  min={0}
                  step={0.01}
                  precision={2}
                />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item
                label="Bill Amount NRA"
                name="bill_amount_nra"
                rules={[
                  {
                    required: true,
                    message: "Bill Amount NRA is required",
                  },
                ]}
              >
                <InputNumber
                  placeholder="Bill Amount NRA"
                  style={{ width: "100%" }}
                  disabled
                  formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                  parser={(value) => value.replace(/\$\s?|(,*)/g, "")}
                  min={0}
                  step={0.01}
                  precision={2}
                />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item
                label="Total Bill Amount"
                name="total_bill_amount"
                rules={[
                  {
                    required: true,
                    message: "Total Bill Amount is required",
                  },
                ]}
              >
                <InputNumber
                  placeholder="Total Bill Amount"
                  style={{ width: "100%" }}
                  disabled
                  formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                  parser={(value) => value.replace(/\$\s?|(,*)/g, "")}
                  min={0}
                  step={0.01}
                  precision={2}
                />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item
                label="Adjustment RA"
                name="adjustment_ra"
                rules={[
                  {
                    required: true,
                    message: "Adjustment RA is required",
                  },
                ]}
              >
                <InputNumber
                  placeholder="Adjustment RA"
                  style={{ width: "100%" }}
                  disabled
                  formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                  parser={(value) => value.replace(/\$\s?|(,*)/g, "")}
                  step={0.01}
                  precision={2}
                />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item
                label="Adjustment NRA"
                name="adjustment_nra"
                rules={[
                  {
                    required: true,
                    message: "Adjustment NRA is required",
                  },
                ]}
              >
                <InputNumber
                  placeholder="Adjustment NRA"
                  style={{ width: "100%" }}
                  disabled
                  formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                  parser={(value) => value.replace(/\$\s?|(,*)/g, "")}
                  step={0.01}
                  precision={2}
                />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item
                label="Date"
                name="adjustment_date"
                rules={[
                  {
                    required: true,
                    message: "Date is required",
                  },
                ]}
              >
                <DatePicker style={{ width: "100%" }} disabled format="YYYY-MM-DD" />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                label="Remarks"
                name="remarks"
                rules={[
                  {
                    required: false,
                    message: "Remarks is required",
                  },
                ]}
              >
                <TextArea rows={3} style={{ resize: "none" }} placeholder="Remarks" disabled={viewMode} />
              </Form.Item>
            </Col>

            <Col span={24}>
              <Card
                style={{
                  marginTop: 16,
                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                  borderRadius: "8px",
                }}
                headStyle={{
                  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  color: "#fff",
                  borderRadius: "8px 8px 0 0",
                  padding: "16px 24px",
                  fontSize: "16px",
                  fontWeight: 600,
                }}
                title="Invoice-wise Expenditure Details"
              >
                <Row gutter={16}>
                  <Col span={8}>
                    <Form.Item
                      label="Invoice Number(s)"
                      name="invoice_ids"
                      rules={[
                        {
                          required: true,
                          message: "Please select at least one Invoice Number",
                        },
                      ]}
                    >
                      <Select
                        mode="multiple"
                        size="small"
                        placeholder="Select Invoice Number(s)"
                        loading={loading.invoices}
                        showSearch
                        optionFilterProp="children"
                        allowClear
                        onChange={handleInvoiceMultiSelectChange}
                        onSearch={(value) => getInvoiceNumbers(value)}
                        onDropdownVisibleChange={(open) => {
                          if (open) {
                            getInvoiceNumbers();
                          }
                        }}
                        filterOption={(input, option) =>
                          String(option?.children ?? "")
                            .toLowerCase()
                            .includes(String(input).toLowerCase())
                        }
                        disabled={viewMode}
                      >
                        {invoices.map((invoice) => (
                          <Option key={invoice.id} value={Number(invoice.id)}>
                            {invoice.invoice_number || `Invoice #${invoice.id}`}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>

                {selectedInvoiceIds.map((invoiceId) => {
                  const invoice = invoices.find((inv) => Number(inv.id) === Number(invoiceId)) || {};
                  const invoiceReceiptableRows = receiptableRows[invoiceId] || [];
                  const invoiceNonReceiptableRows = nonReceiptableRows[invoiceId] || [];

                  return (
                    <Card
                      key={invoiceId}
                      style={{ marginTop: 16, borderRadius: 8, boxShadow: "0 1px 4px rgba(0,0,0,0.08)" }}
                      type="inner"
                      title={
                        <div style={{ display: "flex", flexDirection: "column" }}>
                          <span style={{ fontWeight: 600 }}>Invoice: {invoice.invoice_number || "N/A"}</span>
                          <div style={{ marginTop: 8, fontSize: 12 }}>
                            <Row gutter={12} align="middle">
                              <Col span={6}>
                                <div>
                                  <strong>Invoice Value</strong>
                                </div>
                                <Input
                                  size="small"
                                  disabled
                                  value={
                                    invoice.invoice_value != null
                                      ? Number(invoice.invoice_value)
                                          .toFixed(2)
                                          .replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                                      : ""
                                  }
                                />
                              </Col>
                              <Col span={6}>
                                <div>
                                  <strong>Invoice Quantity</strong>
                                </div>
                                <Input
                                  size="small"
                                  disabled
                                  value={invoice.quantity_ctn != null ? invoice.quantity_ctn : ""}
                                />
                              </Col>
                              <Col span={6}>
                                <div>
                                  <strong>B2B LC Number</strong>
                                </div>
                                <Input
                                  size="small"
                                  disabled
                                  value={invoice.b2blc_info?.b2b_lc_number || ""}
                                />
                              </Col>
                              <Col span={6}>
                                <div>
                                  <strong>LC Date</strong>
                                </div>
                                <Input
                                  size="small"
                                  disabled
                                  value={
                                    invoice.b2blc_info?.b2b_lc_date
                                      ? moment(invoice.b2blc_info.b2b_lc_date).format("YYYY-MM-DD")
                                      : ""
                                  }
                                />
                              </Col>
                            </Row>
                            <Row gutter={12} align="middle" style={{ marginTop: 8 }}>
                              <Col span={6}>
                                <div>
                                  <strong>Sales Contract No</strong>
                                </div>
                                <Select
                                  size="small"
                                  style={{ width: "100%" }}
                                  placeholder="Select Sales Contract"
                                  loading={loadingSalesContracts}
                                  showSearch
                                  optionFilterProp="label"
                                  value={invoiceSalesContractIds[invoiceId] || invoice.sales_contract_id || undefined}
                                  onChange={(value) =>
                                    setInvoiceSalesContractIds((prev) => ({
                                      ...prev,
                                      [invoiceId]: value,
                                    }))
                                  }
                                  disabled={viewMode}
                                  options={salesContractOptions.map((contract) => ({
                                    value: contract.id,
                                    label: contract.label,
                                  }))}
                                  onDropdownVisibleChange={(open) => {
                                    if (open && salesContractOptions.length === 0) {
                                      fetchSalesContracts();
                                    }
                                  }}
                                  filterOption={(input, option) =>
                                    String(option?.label ?? "")
                                      .toLowerCase()
                                      .includes(input.toLowerCase())
                                  }
                                />
                              </Col>
                              <Col span={6}>
                                <div>
                                  <strong>ETA Date</strong>
                                </div>
                                <Input
                                  size="small"
                                  disabled
                                  value={invoice.eta ? moment(invoice.eta).format("YYYY-MM-DD") : ""}
                                />
                              </Col>
                              <Col span={6}>
                                <div>
                                  <strong>Berthing Date</strong>
                                </div>
                                <Input
                                  size="small"
                                  disabled
                                  value={
                                    invoice.berthing_date ? moment(invoice.berthing_date).format("YYYY-MM-DD") : ""
                                  }
                                />
                              </Col>
                              <Col span={6}>
                                <div>
                                  <strong>Delivery Date</strong>
                                </div>
                                <Input
                                  size="small"
                                  disabled
                                  value={
                                    invoice.delivery_date ? moment(invoice.delivery_date).format("YYYY-MM-DD") : ""
                                  }
                                />
                              </Col>
                            </Row>
                            <Row gutter={12} align="middle" style={{ marginTop: 8 }}>
                              <Col span={6}>
                                <div>
                                  <strong>Loading Point</strong>
                                </div>
                                <Input size="small" disabled value={invoice.loading_point || ""} />
                              </Col>
                              <Col span={6}>
                                <div>
                                  <strong>Bill of Entry No</strong>
                                </div>
                                <Input
                                  size="small"
                                  placeholder="Enter Bill of Entry No"
                                  value={invoiceBillEntryNos[invoiceId] || ""}
                                  onChange={(e) =>
                                    setInvoiceBillEntryNos((prev) => ({
                                      ...prev,
                                      [invoiceId]: e.target.value,
                                    }))
                                  }
                                  disabled={viewMode}
                                />
                              </Col>
                              <Col span={6}>
                                <div>
                                  <strong>BL/AWBL/TR/CHALAN No.</strong>
                                </div>
                                <Input
                                  size="small"
                                  placeholder="Enter BL/AWBL/TR/CHALAN No."
                                  value={invoiceExpNumbers[invoiceId] || ""}
                                  onChange={(e) =>
                                    setInvoiceExpNumbers((prev) => ({
                                      ...prev,
                                      [invoiceId]: e.target.value,
                                    }))
                                  }
                                  disabled={viewMode}
                                />
                              </Col>
                               <Col span={6}>
                                <div>
                                  <strong>Doc. Receive Date & Time (C&F)</strong>
                                </div>
                                <DatePicker
                                  size="small"
                                  style={{ width: "100%" }}
                                  format="YYYY-MM-DD HH:mm:ss"
                                  showTime={{ format: "HH:mm:ss" }}
                                  value={invoiceDocReceiveDates[invoiceId] || null}
                                  onChange={(date) =>
                                    setInvoiceDocReceiveDates((prev) => ({
                                      ...prev,
                                      [invoiceId]: date,
                                    }))
                                  }
                                  disabled={viewMode}
                                  placeholder="Select date & time"
                                />
                              </Col>
                              <Col span={6}>
                                <div>
                                  <strong>Bill Adjust Date</strong>
                                </div>
                                <DatePicker
                                  size="small"
                                  style={{ width: "100%" }}
                                  format="YYYY-MM-DD"
                                  value={invoiceBillAdjustDates[invoiceId] || moment()}
                                  onChange={(date) =>
                                    setInvoiceBillAdjustDates((prev) => ({
                                      ...prev,
                                      [invoiceId]: date || moment(),
                                    }))
                                  }
                                  disabled={viewMode}
                                  placeholder="Select date"
                                />
                              </Col>
                            </Row>
                            {/* <Row gutter={12} align="middle" style={{ marginTop: 8 }}>
                             
                            </Row> */}
                          </div>
                        </div>
                      }
                    >
                      <Row gutter={8}>
                        <Col span={12}>
                          <Card
                            title={
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                  width: "100%",
                                  gap: 8,
                                }}
                              >
                                <span style={{ fontSize: 12, lineHeight: 1.2 }}>
                                  Receiptable Details (Expenditure)
                                </span>
                                <Button
                                  type="primary"
                                  icon={<PlusOutlined />}
                                  onClick={() => addReceiptableRow(invoiceId)}
                                  size="small"
                                  style={{
                                    borderRadius: "4px",
                                    background: "#fff",
                                    color: "#667eea",
                                    borderColor: "#fff",
                                    fontSize: 12,
                                    height: 24,
                                    padding: "0 8px",
                                  }}
                                  disabled={viewMode}
                                >
                                  Add Row
                                </Button>
                              </div>
                            }
                            size="small"
                            headStyle={{
                              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                              color: "#fff",
                              borderRadius: "4px 4px 0 0",
                              padding: "4px 10px",
                              fontSize: "12px",
                              fontWeight: 600,
                              minHeight: 32,
                            }}
                            bodyStyle={{
                              padding: "8px",
                            }}
                          >
                            <Form.Item
                              style={{ marginBottom: 0 }}
                              rules={[
                                {
                                  validator: () => {
                                    if (!validateReceiptableRows()) {
                                      return Promise.reject(
                                        new Error(
                                          "Amount is required and must be greater than 0 when type is selected",
                                        ),
                                      );
                                    }
                                    return Promise.resolve();
                                  },
                                },
                              ]}
                            >
                              <Table
                                columns={getReceiptableColumns(invoiceId)}
                                dataSource={invoiceReceiptableRows}
                                rowKey="id"
                                pagination={false}
                                bordered
                                size="small"
                                style={{
                                  borderRadius: "4px",
                                  overflow: "hidden",
                                  fontSize: 12,
                                }}
                                summary={() => {
                                  const total = getTotalReceiptable(invoiceId);
                                  return (
                                    <Table.Summary fixed>
                                      <Table.Summary.Row
                                        style={{
                                          backgroundColor: "#f5f5f5",
                                        }}
                                      >
                                        <Table.Summary.Cell index={0} colSpan={1}>
                                          <strong style={{ fontSize: "12px", color: "#1890ff" }}>
                                            Total Receiptable (Expenditure)
                                          </strong>
                                        </Table.Summary.Cell>
                                        <Table.Summary.Cell index={1}>
                                          <strong style={{ fontSize: "12px", color: "#1890ff" }}>
                                            {total.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                                          </strong>
                                        </Table.Summary.Cell>
                                        <Table.Summary.Cell index={2} />
                                        <Table.Summary.Cell index={3} />
                                      </Table.Summary.Row>
                                    </Table.Summary>
                                  );
                                }}
                              />
                            </Form.Item>
                          </Card>
                        </Col>

                        <Col span={12}>
                          <Card
                            title={
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                  width: "100%",
                                  gap: 8,
                                }}
                              >
                                <span style={{ fontSize: 12, lineHeight: 1.2 }}>
                                  Non Receiptable Amount (Expenditure)
                                </span>
                                <Button
                                  type="primary"
                                  icon={<PlusOutlined />}
                                  onClick={() => addNonReceiptableRow(invoiceId)}
                                  size="small"
                                  style={{
                                    borderRadius: "4px",
                                    background: "#fff",
                                    color: "#667eea",
                                    borderColor: "#fff",
                                    fontSize: 12,
                                    height: 24,
                                    padding: "0 8px",
                                  }}
                                  disabled={viewMode}
                                >
                                  Add Row
                                </Button>
                              </div>
                            }
                            size="small"
                            headStyle={{
                              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                              color: "#fff",
                              borderRadius: "4px 4px 0 0",
                              padding: "4px 10px",
                              fontSize: "12px",
                              fontWeight: 600,
                              minHeight: 32,
                            }}
                            bodyStyle={{
                              padding: "8px",
                            }}
                          >
                            <Form.Item
                              style={{ marginBottom: 0 }}
                              rules={[
                                {
                                  validator: () => {
                                    if (!validateNonReceiptableRows()) {
                                      return Promise.reject(
                                        new Error(
                                          "Amount is required and must be greater than 0 when type is selected",
                                        ),
                                      );
                                    }
                                    return Promise.resolve();
                                  },
                                },
                              ]}
                            >
                              <Table
                                columns={getNonReceiptableColumns(invoiceId)}
                                dataSource={invoiceNonReceiptableRows}
                                rowKey="id"
                                pagination={false}
                                bordered
                                size="small"
                                style={{
                                  borderRadius: "4px",
                                  overflow: "hidden",
                                  fontSize: 12,
                                }}
                                summary={() => {
                                  const total = getTotalNonReceiptable(invoiceId);
                                  return (
                                    <Table.Summary fixed>
                                      <Table.Summary.Row
                                        style={{
                                          backgroundColor: "#f5f5f5",
                                        }}
                                      >
                                        <Table.Summary.Cell index={0} colSpan={1}>
                                          <strong style={{ fontSize: "12px", color: "#1890ff" }}>
                                            Total Non Receiptable (Expenditure)
                                          </strong>
                                        </Table.Summary.Cell>
                                        <Table.Summary.Cell index={1}>
                                          <strong style={{ fontSize: "12px", color: "#1890ff" }}>
                                            {total.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                                          </strong>
                                        </Table.Summary.Cell>
                                        <Table.Summary.Cell index={2} />
                                        <Table.Summary.Cell index={3} />
                                      </Table.Summary.Row>
                                    </Table.Summary>
                                  );
                                }}
                              />
                            </Form.Item>
                          </Card>
                        </Col>
                      </Row>
                    </Card>
                  );
                })}
              </Card>
            </Col>

            <Col span={24}>
              <IOUDocumentUpload
                DOC_ADD={IOU_ATTACHMENT_UPLOAD}
                setFileList={setFileList}
                filesList={filesList}
                view={viewMode}
                userId={null}
              />
            </Col>

            {/* <Col span={24}>
              {(userType === "Head of Commercial & Shipping" ||
                userType === "Director") && (
                <Form.Item
                  label="Approval Status"
                  name="approval_status"
                  rules={[
                    {
                      required: true,
                      message: "Please select approval status",
                    },
                  ]}
                >
                  <Radio.Group disabled={isFieldDisabled("approval_status")}>
                    <Radio value="approved">Approved</Radio>
                    <Radio value="pending">Pending</Radio>
                    <Radio value="rejected">Rejected</Radio>
                  </Radio.Group>
                </Form.Item>
              )}
            </Col> */}
          </Row>
        </Form>
      </Card>
      {/* Comment Modal */}
      <Modal
        title={commentModal.comment ? "Edit Comment" : "Add Comment"}
        open={commentModal.visible}
        onOk={saveComment}
        onCancel={closeCommentModal}
        okText="Save"
        cancelText="Cancel"
        width={600}
      >
        <Form.Item label="Comment">
          <TextArea
            rows={4}
            placeholder="Enter your comment here..."
            value={commentModal.comment}
            onChange={(e) =>
              setCommentModal((prev) => ({
                ...prev,
                comment: e.target.value,
              }))
            }
            disabled={viewMode}
          />
        </Form.Item>
      </Modal>
      <div className="my-2">
        <UploadDocument
          record={editInfo}
          setFileList={setFileList}
          filesList={filesList}
          getIOUCostInfo={getIOUCostInfo}
          view={viewMode}
          iouId={selectedIouId || form.getFieldValue("iou_id")}
        />
      </div>
    </div>
  );
});

IOUAdjustmentImportForm.displayName = "IOUCostForm";

export default IOUAdjustmentImportForm;
